import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { MessageService } from 'primeng/api';

import { ActionKeys } from '../../../Common/Forms/enums/keys.enum';
import { AgprComponentStyleEnum } from '../../../Common/Basic/enums/social-analysis-types.enum';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { download } from '../../../Common/Misc/helpers/download/download.helper';
import { FileDownloadsApi } from '../../../api/fileDownloads/file-downloads.api';
import { FileDownloadSummaryApiModel } from '../../../api/accounts/models/file-download-summary-api.model';
import { FileDownloadSummaryModel, FileDownloadStatusEnum } from '../../../Common/Forms/models/file-download.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { MediaServiceApi } from '../../../api/mediaService/media-service.api';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { TableColumnInputEnum } from '../../../Common/Forms/enums/table-column-input-type.enum';
import { TableColumnModel } from '../../../Common/Forms/models/table-column.model';
import { WebsocketAwareNeptuneService, ResourceType } from '../../../Bootstrap/services/websocket-aware-neptune.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import moment from 'moment-timezone';


@Component({
    selector: 'agpr-file-downloads',
    templateUrl: './file-downloads.component.html',
    styleUrls: ['file-downloads.component.scss'],
    standalone: false
})
export class FileDownloadsComponent implements OnInit, AfterViewInit, OnDestroy {

    files: FileDownloadSummaryModel[] = [];
    fileIdToDownload: string;
    columnDefs: TableColumnModel[] = [];
    dateFormat: string;

    subscriptions: Subscription[] = [];

    readonly fileState = FileDownloadStatusEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly fileNameColumn: TableColumnModel = {
        heading: 'FileDownloads.fileName',
        field: 'name',
        width: 420,
    };
    readonly requestDateColumn: TableColumnModel = {
        heading: 'FileDownloads.requestDate',
        field: 'requestDateDisplayString',
        width: 200
    };
    readonly expiryDateColumn: TableColumnModel = {
        heading: 'FileDownloads.expiryDate',
        field: 'expiryDateDisplayString',
        width: 200
    };
    readonly statusCloumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        heading: 'FileDownloads.status',
        field: 'status',
        width: 55,
        cellStyleClass: 'agpr-fileDownloads-iconCell'

    };
    readonly fileStatusToIconMap: Map<FileDownloadStatusEnum, string> = new Map([
        [FileDownloadStatusEnum.READY_TO_DOWNLOAD, 'fa-regular fa-duotone fa-download'],
        [FileDownloadStatusEnum.IN_PROGRESS, 'fa-kit fa-agpr-in-progress'],
        [FileDownloadStatusEnum.FAILED, 'fa-regular fa-duotone fa-circle-exclamation']
    ]);
    readonly fileStatusToTooltipMap: Map<FileDownloadStatusEnum, string> = new Map([
        [FileDownloadStatusEnum.READY_TO_DOWNLOAD, 'FileDownloads.action.download'],
        [FileDownloadStatusEnum.IN_PROGRESS, 'FileDownloads.status.inProgress'],
        [FileDownloadStatusEnum.FAILED, 'Filedownloads.status.failed']
    ]);

    @ViewChild('statusCell') public statusCellTemplate: TemplateRef<any>;

    constructor(
        public localizationService: LocalizationService,
        public fileDownloadsApi: FileDownloadsApi,
        public mediaServiceApi: MediaServiceApi,
        public messageService: MessageService,
        public coreDialogService: CoreDialogService,
        public websocketEvents: WebsocketAwareNeptuneService,
        public router: Router,
        public currentUserStateService: CurrentUserStateService
    ) {
        this.subscriptions.push(
            this.router.events.subscribe((e) => {
                if (e instanceof NavigationEnd) {
                    const urlParse: RegExp = /FileDownloads\?referenceId=(.+)/;
                    const matches = e.urlAfterRedirects.match(urlParse);
                    if (matches && matches[1]) {
                        this.fileIdToDownload = matches[1];
                    }
                }
            }),
            this.websocketEvents.getWebsocketMessages().subscribe(message => {
                if (message?.resourceType === ResourceType.media || message?.resourceType === ResourceType.filedownloadsMonitoring) {
                    this.loadFileList();
                }
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.dateFormat = prefs.getDateFormatString(true, true, true);
            })
        );
    }

    ngOnInit(): void {
        this.loadFileList();
    }

    ngAfterViewInit(): void {
        this.statusCloumn.customTemplate = this.statusCellTemplate;
        requestAnimationFrame(() => {
            this.columnDefs = [
                this.fileNameColumn,
                this.requestDateColumn,
                this.expiryDateColumn,
                this.statusCloumn
            ];
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    setFiles(files: FileDownloadSummaryApiModel[]): void {
        this.files = files.map(file => new FileDownloadSummaryModel(file));
        this.files.forEach(file => {
            file.name += '.csv';
            file.statusStyleClass = this.fileStatusToIconMap.get(file.status);
            file.statusTooltipText = this.localizationService.transform(this.fileStatusToTooltipMap.get(file.status));
            file.requestDateDisplayString = moment(file.requestDateMS).format(this.dateFormat);
            file.expiryDateDisplayString = file.expiryDateMS && file.status !== FileDownloadStatusEnum.FAILED
                ? moment(file.expiryDateMS).format(this.dateFormat) : 'N/A';
        });
        this.sortFileRows();
        if (this.fileIdToDownload) {
            this.downloadFile(this.fileIdToDownload);
            this.fileIdToDownload = null;
        }
    }

    sortFileRows(): void {
        const pendingFiles = this.getSortedFilesByStatus(FileDownloadStatusEnum.IN_PROGRESS);
        const readyFiles = this.getSortedFilesByStatus(FileDownloadStatusEnum.READY_TO_DOWNLOAD);
        const failedFiles = this.getSortedFilesByStatus(FileDownloadStatusEnum.FAILED);
        this.files = pendingFiles.concat(readyFiles, failedFiles);
    }

    getSortedFilesByStatus(status: FileDownloadStatusEnum): FileDownloadSummaryModel[] {
        return this.files.filter(file => file.status === status)
            .sort((a, b) => b.requestDateMS - a.requestDateMS);
    }

    downloadFile(mediaId: string, event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        const selectedFile = this.files.find(file => file.mediaId === mediaId);
        if (!selectedFile || selectedFile.status !== FileDownloadStatusEnum.READY_TO_DOWNLOAD) {
            return;
        }
        this.mediaServiceApi.getFiles([mediaId]).subscribe(file => {
            download.url(file.mediaResults[0].url, file.mediaResults[0].name + '.csv');
        });
    }

    deleteFile(selectedFile: FileDownloadSummaryModel): void {
        const deletedFileIndex = this.files.findIndex(file => file.uuid === selectedFile.uuid);
        if (deletedFileIndex === -1) {
            return;
        }

        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'fileDownloads.deleteFileConfirmDialog',
                heading: 'FileDownloads.deleteConfirmDialog.heading',
                message: this.localizationService.transform('FileDownloads.deleteConfirmDialog.message', {
                    fileName: selectedFile.name
                }),
                okLabel: 'Social.intelligence.themeManagement.themeCard.deleteTheme.okLabel',
                style: AgprComponentStyleEnum.social
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.fileDownloadsApi.deleteQueuedFile(selectedFile.uuid).subscribe({
                            next: () => {
                                this.files.splice(deletedFileIndex, 1);
                                this.files = this.files.slice();
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: 'FileDownloads.action.delete.success.summary',
                                    detail: this.localizationService.transform(
                                        'FileDownloads.action.delete.success.detail',
                                        { fileName: selectedFile.name }
                                    )
                                });
                            },
                            error: () => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.error,
                                    summary: 'FileDownloads.action.delete.failure.summary',
                                    detail: this.localizationService.transform(
                                        'FileDownloads.action.delete.failure.detail',
                                        { fileName: selectedFile.name }
                                    )
                                });
                            }
                        });
                    }
                }
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    loadFileList(): void {
        this.fileDownloadsApi.getQueuedFiles().subscribe(files => {
            this.setFiles(files);
        });
    }
}
